import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { isPositionShort } from 'api/apiUtils';
import { useMarketTickerQuery } from 'api/queries/tickersQueries';
import { usePositionQuery } from 'api/queries/walletPositionsQueries';
import { ApiTriggerType } from 'api/types';
import RouterLink from 'components/basic/Link/RouterLink';
import ModalContent from 'components/basic/modal/components/ModalContent';
import ModalFooterButtons from 'components/basic/modal/components/ModalFooterButtons';
import SimpleModalOrMobilePage from 'components/basic/modal/SimpleModalOrMobilePage';
import DataPair from 'components/basic/panels/DataPair';
import ErrorPanel from 'components/basic/panels/ErrorPanel';
import { DebugPanel } from 'components/dev/DebugPanel';
import { cancelOrderWithSignModal } from 'components/orders/orderCancelUtils';
import { checkStopOrderInstantExecutionError } from 'components/orders/orderUtil';
import {
  useFetchInitialMarginFractionForWallet,
  useFetchInitialMarginFractionOverride,
} from 'components/trading/BuySellPanel/leverage/leverageUtils';
import { MobileDataPairCoinIcon } from 'config/coinIcons/CoinIcon';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { formatNumber } from 'lib/display/formatting';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';
import { sanitizeAndTruncateNumber } from 'lib/numbers';
import {
  getDashboardUrl,
  useSelectMarketTickSize,
} from 'reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from 'reduxState/storeCore';

import ConditionalTPSLSideTabs from './ConditionalTPSLSideTabs';
import Explanation from './Explanation';
import { useConnectOrderbookAndGetMiddlePrice } from './orderbookMiddlePrice';
import TPSLRemoveButton from './TPSLRemoveButton';
import TPSLSliderAndInput from './TPSLSliderAndInput';
import {
  prepareExecutionOfTpSlOrders,
  useCurrentTpSlOrders,
} from './tpSlUtils';
import TriggerDropdown from './TriggerDropdown';

const SmallText = styled.div`
  font-size: var(--small);
`;

const LabelFlex = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0 8px;
`;

const FooterText = styled.div`
  margin: 24px 0 16px;
  color: var(--gray4c);
  white-space: normal;
  font-size: var(--small);
  font-style: normal;
  font-weight: normal;
  line-height: normal;
`;

type FutureTpsl = {
  /** Initial take price type only for EDITING unsaved conditional TPSL in BuyPanel  */
  initialTakeProfitType?: 'index' | 'last';
  /** Initial stop price type only for EDITING unsaved conditional TPSL in BuyPanel */
  initialStopLossType?: 'index' | 'last';

  /** Undefined for new orders, defined for existing */
  parentOrderId: string | undefined;
  parentOrderSide: 'long' | 'short' | undefined;
  parentBuyQuantity: string | undefined;
  parentSellQuantity: string | undefined;
  parentPriceType: 'market' | 'limit' | 'stop';
  parentPrice: string | undefined;
  /** Do not automatically sign orders, but delegate this action to the parent */
  onSaveOverride: (params: {
    side: 'long' | 'short';
    takeProfit?: {
      triggerType: 'index' | 'last';
      triggerPrice: number;
    };
    stopLoss?: {
      triggerType: 'index' | 'last';
      triggerPrice: number;
    };
  }) => Promise<void>;
};

type Props = {
  market: string;
  initialTakeProfitPrice?: number | string;
  initialStopLossPrice?: number | string;
  onClose: () => void;
  futureTpSl?: FutureTpsl;
};

/** Specification https://docs.google.com/document/d/1WXbIxalYsrbwciwM8b9tZv2VBl_9H7XBSIVpSrunZp4 */
export default function TPSLModal({
  market,
  initialTakeProfitPrice,
  initialStopLossPrice,
  futureTpSl,
  onClose,
}: Props) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const wallet = useSelector(state => state.wallet);
  const delegatedWallet = useSelector(
    state => state.delegatedKey.authorization
  );
  const marketsLeverageParamsMap = useSelector(
    state => state.markets.listedMarkets.marketsLeverageParams
  );
  const marketLeverageParams = marketsLeverageParamsMap?.[market];
  const initialMarginFractionOverride = useFetchInitialMarginFractionOverride({
    walletAddress: wallet.walletAddress,
    market,
    isVault: false,
  });

  const [baseSymbol, quoteSymbol] = market.split('-');

  const { data: positionData } = usePositionQuery({
    market: futureTpSl ? undefined : market,
  });
  const tickSize = useSelectMarketTickSize(market);
  const tickSizeDecimals = tickSize?.visibleDecimals;

  const { data: marketTicker } = useMarketTickerQuery({
    market,
    websocketSubscribe: true,
  });

  const [takeProfitTriggerBy, setTakeProfitTriggerBy] = useState<
    'byChange' | 'byROI'
  >('byChange');
  const [stopLossTriggerBy, setStopLossTriggerBy] = useState<
    'byChange' | 'byROI'
  >('byChange');

  const initialMarginFraction = useFetchInitialMarginFractionForWallet({
    market:
      takeProfitTriggerBy === 'byROI' || stopLossTriggerBy === 'byROI'
        ? market
        : undefined,
    walletAddress: wallet.walletAddress,
    isVault: false,
  });

  // Only used with future order TPSL
  const [sideNewOrder, setSideNewOrder] = useState<'long' | 'short'>('long');

  /** Conditional TPSL override for Future order */
  const overrideSide = futureTpSl?.parentOrderSide || sideNewOrder;

  const [errorMessage, setErrorMessage] = useState('');

  const { stopLossOrder, takeProfitOrder } = useCurrentTpSlOrders(
    market,
    futureTpSl?.parentOrderId
  );

  const storedTakeProfitTriggerPrice =
    takeProfitOrder?.triggerPrice?.toString();
  const storedStopLossTriggerPrice = stopLossOrder?.triggerPrice?.toString();

  const defaultTakeProfitPrice = initialTakeProfitPrice
    ? sanitizeAndTruncateNumber(initialTakeProfitPrice)
    : storedTakeProfitTriggerPrice
    ? sanitizeAndTruncateNumber(storedTakeProfitTriggerPrice, tickSizeDecimals)
    : '';

  const [takeProfitPrice, setTakeProfitPrice] = useState(
    defaultTakeProfitPrice
  );
  const [takeProfitTriggerType, setTakeProfitTriggerType] =
    useState<ApiTriggerType>(
      takeProfitOrder?.triggerType ||
        futureTpSl?.initialTakeProfitType ||
        'index'
    );
  useEffect(() => {
    // Propagate Update from parent (eg for removal or external change)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTakeProfitPrice(defaultTakeProfitPrice);
  }, [defaultTakeProfitPrice, initialTakeProfitPrice]);

  const [processing, setProcessing] = useState(false);

  const defaultStopLossPrice = initialStopLossPrice
    ? sanitizeAndTruncateNumber(initialStopLossPrice)
    : storedStopLossTriggerPrice
    ? sanitizeAndTruncateNumber(storedStopLossTriggerPrice, tickSizeDecimals)
    : '';

  const [stopLossPrice, setStopLossPrice] = useState(defaultStopLossPrice);
  const [stopLossTriggerType, setStopLossTriggerType] =
    // TODO: this (and same above) is only working because orders are fetched early
    useState<ApiTriggerType>(
      stopLossOrder?.triggerType || futureTpSl?.initialStopLossType || 'index'
    );

  useEffect(() => {
    // Propagate Update from parent (eg for removal or external change)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStopLossPrice(defaultStopLossPrice);
  }, [defaultStopLossPrice, initialStopLossPrice]);

  const orderbookPriceNeeded = futureTpSl
    ? // For Conditional TPSL it's only needed for Market option
      futureTpSl.parentPriceType === 'market'
    : takeProfitTriggerType === 'last' || stopLossTriggerType === 'last';

  const { middlePrice, displayedOrderbookMarket, otherMarketOrderbookActive } =
    useConnectOrderbookAndGetMiddlePrice({
      market,
      startOrderbook: orderbookPriceNeeded,
    });

  const lastPrice = marketTicker ? marketTicker.close : undefined;
  const indexPrice = marketTicker ? marketTicker.indexPrice : undefined;
  // Last price only in Modal as we show it as Label&Value
  const midOrLastPrice = middlePrice || lastPrice || undefined;

  const maximumPositionSizes = useSelector(
    state => state.markets.listedMarkets.maximumPositionSizes
  );
  const maximumPositionSize = maximumPositionSizes[market];

  const handleSetSideNewOrder = (side: 'long' | 'short') => {
    setSideNewOrder(side);
    setTakeProfitPrice(defaultTakeProfitPrice);
    setStopLossPrice(defaultStopLossPrice);
  };

  const positionObj = useMemo(() => {
    if (futureTpSl) {
      return {
        isShort: overrideSide === 'short',
      };
    }
    if (!positionData) {
      return {};
    }
    const isShort = isPositionShort(positionData);
    const { entryPrice, quantity } = positionData;
    return { isShort, entryPrice, quantity };
  }, [futureTpSl, overrideSide, positionData]);

  const {
    isShort,
    // TODO: rename to work with both Conditional and Postion TPSL ?
    entryPrice: positionEntryPrice,
    quantity: positionQuantity,
  } = positionObj;

  if (isShort === undefined) {
    console.log('[TPSLModal] WARN No positionData');
    return null;
  }

  const takeProfitPriceNumber = Number(takeProfitPrice || 0);
  const stopLossPriceNumber = Number(stopLossPrice || 0);

  const parentOrMidPrice = futureTpSl
    ? futureTpSl.parentPriceType === 'market'
      ? midOrLastPrice
      : futureTpSl.parentPrice
    : midOrLastPrice;

  const currentOrParentPriceForTakeProfit =
    takeProfitTriggerType === 'index' && !futureTpSl
      ? indexPrice
      : parentOrMidPrice;
  const currentPriceForStopLoss =
    stopLossTriggerType === 'index' && !futureTpSl
      ? indexPrice
      : parentOrMidPrice;

  if (!currentOrParentPriceForTakeProfit || !currentPriceForStopLoss) {
    console.log('[TPSLModal] WARN No current price');
    return null;
  }

  const takeProfitError = checkStopOrderInstantExecutionError({
    type: 'takeProfit',
    price: takeProfitPriceNumber,
    positionIsShort: isShort,
    currentPrice: currentOrParentPriceForTakeProfit,
  });
  const stopLossError = checkStopOrderInstantExecutionError({
    type: 'stopLoss',
    price: stopLossPriceNumber,
    positionIsShort: isShort,
    currentPrice: currentPriceForStopLoss,
  });

  const changedStopLoss =
    (stopLossOrder && stopLossTriggerType !== stopLossOrder?.triggerType) ||
    stopLossPriceNumber !== Number(stopLossOrder?.triggerPrice || 0);

  const changedTakeProfit =
    (takeProfitOrder &&
      takeProfitTriggerType !== takeProfitOrder?.triggerType) ||
    takeProfitPriceNumber !== Number(takeProfitOrder?.triggerPrice || 0);

  const takeProfitErrorMessage = takeProfitError
    ? `Trigger price should be ${isShort ? 'lower' : 'higher'} than ${
        futureTpSl?.parentPriceType || takeProfitTriggerType
      } price`
    : undefined;

  const stopLossErrorMessage = stopLossError
    ? `Trigger price should be ${isShort ? 'higher' : 'lower'} than ${
        futureTpSl?.parentPriceType || stopLossTriggerType
      } price`
    : undefined;

  const tpslOrderSide = isShort ? 'buy' : 'sell';

  const onConfirmAsync = async () => {
    if (!maximumPositionSize) {
      setErrorMessage('Unknown market max position');
      return;
    }
    setProcessing(true);
    if (futureTpSl) {
      futureTpSl
        .onSaveOverride({
          side: overrideSide,
          stopLoss:
            changedStopLoss && stopLossPriceNumber
              ? {
                  triggerPrice: stopLossPriceNumber,
                  triggerType: stopLossTriggerType,
                }
              : undefined,
          takeProfit:
            changedTakeProfit && takeProfitPriceNumber
              ? {
                  triggerPrice: takeProfitPriceNumber,
                  triggerType: takeProfitTriggerType,
                }
              : undefined,
        })
        .catch(error => {
          console.warn(error);
          setErrorMessage(makeEthersErrorHumanRedable(error));
          setProcessing(false);
        });
      return;
    }
    // Normal TPSL (on existing position), 1. Cancel first 2. Place new
    try {
      if (changedTakeProfit && takeProfitOrder) {
        await cancelOrderWithSignModal(
          takeProfitOrder.orderId,
          wallet,
          delegatedWallet
        );
      }
      if (changedStopLoss && stopLossOrder) {
        await cancelOrderWithSignModal(
          stopLossOrder.orderId,
          wallet,
          delegatedWallet
        );
      }
    } catch (error) {
      console.warn(error);
      setErrorMessage(makeEthersErrorHumanRedable(error));
      setProcessing(false);
      return;
    }
    const executeTpSlOrder = prepareExecutionOfTpSlOrders({
      baseSymbol,
      quoteSymbol,
      delegatedWallet,
      wallet,
      side: tpslOrderSide,
      maximumPositionSize,
    });
    try {
      if (
        changedTakeProfit &&
        takeProfitPriceNumber &&
        takeProfitPriceNumber > 0
      ) {
        await executeTpSlOrder({
          orderType: 'takeProfitMarket',
          price: takeProfitPriceNumber,
          triggerType: takeProfitTriggerType,
        });
      }
      if (changedStopLoss && stopLossPriceNumber && stopLossPriceNumber > 0) {
        await executeTpSlOrder({
          orderType: 'stopLossMarket',
          price: stopLossPriceNumber,
          triggerType: stopLossTriggerType,
        });
      }
    } catch (error) {
      console.warn(error);
      setErrorMessage(makeEthersErrorHumanRedable(error));
      setProcessing(false);
      return;
    }
    setProcessing(false);
    onClose();
  };

  // For longs, the take profit price must be higher than the current value of the reference price
  // For shorts, the take profit price must be lower than the current value of the reference price
  // If the user tries to enter a price that is invalid, display the message “Trigger price should be (higher/lower) than (reference) price”
  // The first field is “higher” for longs, and “lower” for shorts
  // The second field contains the reference price selected (index or last)

  const saveDisabled =
    (!changedTakeProfit && !changedStopLoss) ||
    processing ||
    !!takeProfitErrorMessage ||
    !!stopLossErrorMessage;

  const futureTpSLQuantity = isShort
    ? `-${futureTpSl?.parentSellQuantity}` // Add sign
    : futureTpSl?.parentBuyQuantity;

  return (
    <SimpleModalOrMobilePage
      size="small"
      title="TP/SL for Entire Position"
      onClose={isMobile ? undefined : onClose}
      onBack={isMobile ? onClose : undefined}
      padding="0"
      verticalAlignOnMobile="centered"
    >
      <form
        onSubmit={
          saveDisabled
            ? undefined
            : event => {
                event.preventDefault();
                onConfirmAsync();
              }
        }
      >
        <ModalContent paddingTop="16px" paddingBottom="0">
          <SmallText>
            <DataPair label="Market" noPadding childrenFontFamily="standard">
              <RouterLink to={getDashboardUrl('katanaEVMChain', market)}>
                {baseSymbol && <MobileDataPairCoinIcon symbol={baseSymbol} />}
                {market}
              </RouterLink>
            </DataPair>
            {futureTpSl?.parentPrice && (
              <DataPair label="Order Price" noPadding>
                {formatNumber(futureTpSl.parentPrice, {
                  fixedDecimals: tickSizeDecimals,
                })}
              </DataPair>
            )}
            {positionEntryPrice && (
              <DataPair label="Entry Price" noPadding>
                {formatNumber(positionEntryPrice, {
                  fixedDecimals: tickSizeDecimals,
                })}
              </DataPair>
            )}
            <DataPair label="Index Price" noPadding>
              {indexPrice
                ? formatNumber(indexPrice, {
                    fixedDecimals: tickSizeDecimals,
                  })
                : null}
            </DataPair>
            <DataPair label="Last Price" noPadding>
              {lastPrice
                ? formatNumber(lastPrice, {
                    fixedDecimals: tickSizeDecimals,
                  })
                : ''}
            </DataPair>
          </SmallText>
          {futureTpSl && !futureTpSl.parentOrderId && (
            <>
              <div style={{ height: 8 }} />
              <ConditionalTPSLSideTabs
                side={sideNewOrder}
                handleChangeSide={handleSetSideNewOrder}
              />
              <div style={{ height: 16 }} />
            </>
          )}
          <LabelFlex>
            <div>Take Profit &nbsp; -</div>
            <TriggerDropdown
              onChange={setTakeProfitTriggerBy}
              selected={takeProfitTriggerBy}
            />
          </LabelFlex>
          <TPSLSliderAndInput
            targetAction={tpslOrderSide}
            targetPrice={takeProfitPrice}
            onChangePrice={setTakeProfitPrice}
            triggerType={takeProfitTriggerType}
            trigger="takeProfit"
            onChangeTriggerType={setTakeProfitTriggerType}
            processing={processing}
            tickSizeDecimals={tickSizeDecimals}
            // currentPrice={currentPriceForTakeProfit}
            // TODO prevent convert
            entryPrice={
              // TODO: remove fallback ?
              positionEntryPrice || currentOrParentPriceForTakeProfit
            }
            initialMarginFraction={initialMarginFraction}
            triggerBy={takeProfitTriggerBy}
            removeButton={
              <TPSLRemoveButton
                valueEmpty={!Number(takeProfitPrice)}
                currentOrder={takeProfitOrder}
                onRemove={() => setTakeProfitPrice('')}
              />
            }
          />
          {takeProfitErrorMessage && (
            <ErrorPanel message={takeProfitErrorMessage} disableWarningIcon />
          )}
          {futureTpSl &&
          futureTpSLQuantity &&
          currentOrParentPriceForTakeProfit ? (
            <Explanation
              disabled={!!takeProfitErrorMessage}
              type="takeProfit"
              price={takeProfitPrice}
              entryPrice={formatNumber(currentOrParentPriceForTakeProfit, {
                fixedDecimals: EXCHANGE_DECIMALS,
              })}
              quantity={futureTpSLQuantity}
              tickSizeDecimals={tickSizeDecimals}
              triggerType={takeProfitTriggerType}
              marketLeverageParams={marketLeverageParams}
              initialMarginFractionOverride={initialMarginFractionOverride}
            />
          ) : positionEntryPrice ? (
            <Explanation
              disabled={!!takeProfitErrorMessage}
              type="takeProfit"
              price={takeProfitPrice}
              entryPrice={positionEntryPrice}
              quantity={positionQuantity}
              tickSizeDecimals={tickSizeDecimals}
              triggerType={takeProfitTriggerType}
              marketLeverageParams={marketLeverageParams}
              initialMarginFractionOverride={initialMarginFractionOverride}
            />
          ) : null}
          <br />
          <LabelFlex>
            <div>Stop Loss &nbsp; -</div>
            <TriggerDropdown
              onChange={setStopLossTriggerBy}
              selected={stopLossTriggerBy}
            />
          </LabelFlex>
          <TPSLSliderAndInput
            targetPrice={stopLossPrice}
            targetAction={tpslOrderSide}
            onChangePrice={setStopLossPrice}
            triggerType={stopLossTriggerType}
            trigger="stopLoss"
            onChangeTriggerType={setStopLossTriggerType}
            processing={processing}
            tickSizeDecimals={tickSizeDecimals}
            // currentPrice={currentPriceForStopLoss}
            entryPrice={positionEntryPrice || currentPriceForStopLoss}
            initialMarginFraction={initialMarginFraction}
            triggerBy={stopLossTriggerBy}
            removeButton={
              <TPSLRemoveButton
                valueEmpty={!Number(stopLossPrice)}
                currentOrder={stopLossOrder}
                onRemove={() => setStopLossPrice('')}
              />
            }
          />
          {stopLossErrorMessage && (
            <ErrorPanel message={stopLossErrorMessage} disableWarningIcon />
          )}
          {futureTpSl && futureTpSLQuantity && currentPriceForStopLoss ? (
            <Explanation
              disabled={!!stopLossErrorMessage}
              type="stopLoss"
              price={stopLossPrice}
              entryPrice={formatNumber(currentPriceForStopLoss, {
                fixedDecimals: EXCHANGE_DECIMALS,
              })}
              quantity={futureTpSLQuantity}
              tickSizeDecimals={tickSizeDecimals}
              triggerType={stopLossTriggerType}
              marketLeverageParams={marketLeverageParams}
              initialMarginFractionOverride={initialMarginFractionOverride}
            />
          ) : positionEntryPrice ? (
            <Explanation
              disabled={!!stopLossErrorMessage}
              type="stopLoss"
              price={stopLossPrice}
              entryPrice={positionEntryPrice}
              quantity={positionQuantity}
              tickSizeDecimals={tickSizeDecimals}
              triggerType={stopLossTriggerType}
              marketLeverageParams={marketLeverageParams}
              initialMarginFractionOverride={initialMarginFractionOverride}
            />
          ) : null}
          <FooterText>
            A market order is automatically executed when the trigger price is
            reached. The market order applies to the entire position. The
            take-profit and stop-loss orders are canceled after closing the
            position.
          </FooterText>
          {errorMessage && (
            <ErrorPanel message={errorMessage} marginBottom={8} />
          )}
        </ModalContent>
        <ModalFooterButtons
          confirmButtonProps={{
            submit: true,
            disabled: saveDisabled,
            children: 'Confirm',
          }}
        />
      </form>
      {!isMobile && (
        <DebugPanel fixedBottomRight>
          Displayed orderbook={displayedOrderbookMarket} | other active
          orderbook=
          {otherMarketOrderbookActive ? 'yes' : 'no'} | Middle Price=
          {formatNumber(middlePrice, { fixedDecimals: tickSizeDecimals })}
          <br />
          Current price for SL: {currentPriceForStopLoss}, TP:{' '}
          {currentOrParentPriceForTakeProfit} | IMF:{' '}
          {Number(initialMarginFraction)}
        </DebugPanel>
      )}
    </SimpleModalOrMobilePage>
  );
}
